/**
 * Centralised API error → human-readable message helper.
 *
 * Why this exists: parts of the app talk to the backend through the typed SDK
 * (`apiGet`/`apiPost`/etc — throws `ApiError` with `.body` and `.message`)
 * while a few legacy paths (`AuthContext.login`, `PasswordResetPages.js`,
 * widget flows) still use axios (throws `AxiosError` with
 * `.response.data.detail`). Without a helper, every catch-handler had to
 * remember the right fallback chain — and Iter-46c proved we drift.
 *
 * `formatApiError(e, fallback)` reads, in order:
 *   1. `e.body.detail`              — typed-SDK error body (FastAPI `detail`)
 *   2. `e.response.data.detail`     — axios error body
 *   3. `e.message`                  — generic Error/SDK fallback string
 *   4. `fallback`                   — caller-supplied last resort
 *
 * `detail` may be either a plain string or a dict like
 * `{code: "...", message: "..."}` (Iter-49 introduced structured MFA errors).
 * When it's a dict we return `.message` so `toast.error` doesn't render
 * "[object Object]".
 *
 * All legs are optional-chained so any malformed input (null, plain
 * Error, fetch TypeError, etc.) safely falls through to `fallback`.
 *
 * @param {unknown} e Error thrown by an API call.
 * @param {string} fallback Message to show when no detail is available.
 * @returns {string}
 */
// Iter-52: FastAPI's Pydantic 422 responses put an ARRAY of error objects
// under `detail` (e.g. `[{loc, msg, type}]`). Without unwrapping the array
// we'd fall through to a generic fallback and the user would see
// "Registration failed" instead of "Password must contain at least one
// number". Handle string | dict | list-of-dicts uniformly.
const _detail = (d) => {
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const msgs = d
      .map((it) => (typeof it === 'string' ? it : it && (it.msg || it.message)))
      .filter(Boolean);
    return msgs.length ? msgs.join('; ') : undefined;
  }
  if (d && typeof d === 'object') return d.message;
  return undefined;
};

export const formatApiError = (e, fallback) =>
  _detail(e?.body?.detail) ||
  _detail(e?.response?.data?.detail) ||
  e?.message ||
  fallback;

/**
 * Returns a machine-readable error code from a FastAPI structured detail
 * (`{code: "mfa_reset_required", ...}`) so callers can branch on it.
 * Returns `null` when the detail is a plain string or missing.
 */
export const apiErrorCode = (e) => {
  const d = e?.body?.detail ?? e?.response?.data?.detail;
  return d && typeof d === 'object' ? d.code || null : null;
};
